import * as React from 'react';
import { FormHelperText, FormControl, Collapse, makeStyles } from '@material-ui/core';
import { OtherTextField } from 'components/common/OtherTextField/OtherTextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';

const useStyles = makeStyles(theme => ({
  dropdownContainer: {
    width: '50%',
    [theme.breakpoints.down(theme.breakpoints.values.mobile)]: {
      width: '100%'
    },
    marginBottom: '1rem',
    '& [class*="MuiFilledInput-input"]': {
      paddingTop: '1rem'
    }
  }
}));

export const AutocompleteDropdown = props => {
  const { dropdownContainer } = useStyles();
  const {
    t,
    id,
    selected: initialSelectedValues,
    onChange,
    inputValidation,
    onValidationError,
    options,
    multiple,
    error: initialError,
    otherText: initialOtherText,
    disabled,
    variant,
    otherTextMaxCharacters
  } = props;

  // State Management
  const [selected, setSelected] = React.useState(initialSelectedValues || []);
  const [error, setError] = React.useState(initialError);
  const [otherText, setOtherText] = React.useState(initialOtherText || '');
  const [otherTextError, setOtherTextError] = React.useState(!initialOtherText);

  // Local Vars
  const possibleValueWithOtherText =
    options.find(possibleValue => {
      return possibleValue.enableOtherText;
    }) || {};
  const showOtherText = selected.indexOf(possibleValueWithOtherText.value) > -1;
  const chipProps = {
    size: 'small',
    variant: 'outlined'
  };
  const taglimit = 2;
  const value = multiple ? options.filter(option => selected.includes(option.value)) : options.filter(option => selected.includes(option.value))[0];

  // Side Effects
  React.useEffect(() => {
    setSelected(initialSelectedValues || []);
  }, [initialSelectedValues]);
  React.useEffect(() => {
    setOtherText(initialOtherText);
    setOtherTextError(!initialOtherText);
  }, [initialOtherText]);

  // Update error if error prop changes
  React.useEffect(() => {
    setError(initialError);
  }, [initialError]);

  // Event Handlers
  const handleSelectionChange = React.useCallback(
    // eslint-disable-next-line no-shadow
    (event, value) => {
      let newValue = [];

      if (value == null) {
        newValue = [];
      } else if (Object.prototype.toString.call(value) === '[object Array]') {
        newValue = value.map(item => item.value);
      } else {
        newValue = [value.value];
      }
      setSelected(newValue);

      // Other Text and Min/Max Error handling
      const otherTextIsRequired = possibleValueWithOtherText.value && newValue.indexOf(possibleValueWithOtherText.value) > -1;
      const otherTextIsMissing = otherTextError && otherTextIsRequired;

      let errorObj = null;
      const numChecked = newValue.length;
      errorObj = inputValidation && numChecked > 0 && inputValidation(numChecked);
      if (errorObj || otherTextIsMissing) {
        onValidationError(newValue, otherTextIsRequired ? otherText : null, errorObj);
      } else {
        onChange(newValue, otherTextIsRequired ? otherText : null);
      }

      const newError = errorObj ? t(errorObj.key, { ...errorObj.args }) : null;
      setError(newError);
    },
    [inputValidation, onChange, onValidationError, otherText, otherTextError, possibleValueWithOtherText.value, t]
  );

  const handleOtherTextChange = React.useCallback(
    e => {
      // eslint-disable-next-line no-shadow
      const { value } = e.target;
      setOtherTextError(!value);
      setOtherText(value);
      if (!value) {
        onValidationError(selected, value);
      } else {
        onChange(selected, value);
      }
    },
    [onChange, onValidationError, selected]
  );

  // Render
  return (
    <>
      <FormControl className={dropdownContainer} variant={variant || 'filled'}>
        <Autocomplete
          id={`auto-ddn-${id}`}
          sx={{
            '& .MuiFilledInput-root': {
              paddingTop: '24px'
            }
          }}
          value={value || null}
          onChange={handleSelectionChange}
          limitTags={taglimit}
          ChipProps={chipProps}
          multiple={multiple}
          options={options}
          includeInputInList
          clearOnEscape
          disableCloseOnSelect={multiple}
          disablePortal
          noOptionsText={t('autocompleteDropdown.noOptionsText')}
          getOptionLabel={option => option.label}
          disabled={disabled}
          // eslint-disable-next-line no-shadow
          renderOption={(props, option) => (
            <li {...props}>
              {multiple && <Checkbox checked={selected.includes(option.value)} value={option.value} />}
              {option.label}
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              variant={variant || 'filled'}
              type="text"
              label={t('autocompleteDropdown.label')}
              placeholder={t('autocompleteDropdown.placeholder')}
            />
          )}
        />
      </FormControl>
      {error && <FormHelperText error>{error}</FormHelperText>}
      <Collapse in={showOtherText}>
        <OtherTextField
          value={otherText}
          onChange={handleOtherTextChange}
          label={possibleValueWithOtherText.defaultOtherText}
          required
          error={otherTextError}
          disabled={disabled}
          maxCharactersAllowed={otherTextMaxCharacters}
        />
      </Collapse>
    </>
  );
};

AutocompleteDropdown.defaultProps = {
  t: k => k,
  options: [],
  selected: [],
  inputValidation: () => null,
  onValidationError: e => e
};
